import { useEffect, useState } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual";

const KEY = "fl:nav-stack";
const load = () => { try { return JSON.parse(sessionStorage.getItem(KEY)) || []; } catch { return []; } };
let stack = load();
let booted = false;
const listeners = new Set();
const persist = () => sessionStorage.setItem(KEY, JSON.stringify(stack));
const commit = () => { persist(); listeners.forEach(fn => fn([...stack])); };

export const useNavStack = () => {
  const [s, setS] = useState(stack);
  useEffect(() => { listeners.add(setS); setS([...stack]); return () => listeners.delete(setS); }, []);
  return s;
};

const restoreScroll = (y) => {
  const started = Date.now();
  let cancelled = false;
  const cancel = () => { cancelled = true; };
  window.addEventListener("wheel", cancel, { once: true, passive: true });
  window.addEventListener("touchstart", cancel, { once: true, passive: true });
  const tick = () => {
    if (cancelled) return;
    window.scrollTo(0, y);
    const fits = document.documentElement.scrollHeight >= y + window.innerHeight;
    if ((Math.abs(window.scrollY - y) > 2 || !fits) && Date.now() - started < 8000) setTimeout(tick, 100);
  };
  requestAnimationFrame(tick);
};

export const ScrollToTop = () => {
  const location = useLocation();
  const navType = useNavigationType();
  const { pathname, search, hash, key } = location;

  useEffect(() => {
    let raf;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const top = stack[stack.length - 1];
        const current = window.history.state?.key || "default";
        if (top && top.key === current) { top.y = window.scrollY; persist(); }
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const entry = { key, path: pathname + search, y: 0 };
    const idx = stack.findIndex(e => e.key === key);
    if (!booted) {
      booted = true;
      if (idx >= 0 && stack[idx].path === entry.path) { const y = stack[idx].y; stack = stack.slice(0, idx + 1); commit(); restoreScroll(y); return; }
      stack = [entry]; commit();
    } else if (navType === "POP" && idx >= 0 && stack[idx].path === entry.path) {
      const y = stack[idx].y;
      stack = stack.slice(0, idx + 1); commit();
      restoreScroll(y);
      return;
    } else if (navType === "REPLACE" && stack.length) {
      stack[stack.length - 1] = entry; commit();
    } else {
      stack.push(entry); commit();
    }
    if (hash && /^#[A-Za-z][\w-]*$/.test(hash)) {
      const el = document.querySelector(hash);
      if (el) { el.scrollIntoView({ behavior: "smooth" }); return; }
    }
    window.scrollTo(0, 0);
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
};
